/**
 * Phaser lazy header menus.
 *
 * The page HTML carries only the header's top-level menu links (see the
 * menu_lazy notes in blocks/_header-menu.liquid). The desktop dropdown panels
 * and the mobile drawer's menu list come from the header's hydration render:
 * the Section Rendering API renders the header with section.index blank, and
 * the keyed morph (data-hydration-key) adds the missing parts in place, so
 * links that are already on the page keep their focus and state.
 *
 * This replaces Horizon's plain `hydrate()` call for the header so the render
 * starts as soon as the shopper reaches a native menu or the drawer. The
 * production FPV desktop concept supplies its own panels and data instead.
 * A drawer-only request leaves desktop panels deferred. A later desktop
 * interaction can hydrate the complete header; failed renders can be retried.
 *
 * Shoppers no longer get it on idle (31 Oct 2026): the render is ~765KB of HTML
 * and ~4,500 elements (the phone drawer and every desktop dropdown), merged into
 * every page whether or not the menus are used. On phones that is a large share
 * of the page's memory. Crawlers still get it on idle, so search engines keep
 * seeing every menu link without anyone touching the header.
 */

import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { trapFocus } from '@theme/focus';

const script = document.querySelector('script[data-phaser-header-section]');
const sectionId = script?.getAttribute('data-phaser-header-section') || '';

/** @type {Promise<void> | null} */
let pending = null;
let done = false;
let pendingScope = '';
let fullRequested = false;
let drawerResponseFailed = false;

// Phaser: search, account and wishlist intent must not fetch the menu tree.
const menuIntentSelector = 'header-menu, header-drawer, [data-pdn-set]';
const intentListeners = new AbortController();
const desktopMenu = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

/**
 * Phaser: mix mode owns the FPV desktop panels and already has its RADIO
 * template in the page. Native panels are only needed for other departments,
 * the drawer, or items handed back after a concept failure.
 * @param {Element} target
 */
function needsNativeMenu(target) {
  if (!target.closest(menuIntentSelector)) return false;
  if (target.closest('header-drawer')) return true;
  // Re-selecting FPV cannot need the native panels either; other department
  // selectors still prewarm them before the shopper reaches their dropdowns.
  const menu = target.closest('header-menu.custom-dropdown-menu, [data-pdn-set="fpv"]');
  return !(
    menu && desktopMenu.matches &&
    document.documentElement.getAttribute('data-menu-concept') === 'mix' &&
    document.documentElement.hasAttribute('data-phaser-desktop-menu') &&
    (document.documentElement.getAttribute('data-phaser-dept') || 'fpv') === 'fpv' &&
    !target.closest('.qd-not')
  );
}

/** @returns {HTMLElement | null} */
function getSection() {
  return sectionId ? document.getElementById(buildSectionSelector(sectionId)) : null;
}

/** True while any lazy placeholder is still on the page. */
function isLazy(scope = 'full') {
  return !!getSection()?.querySelector(scope === 'drawer' ? 'header-drawer [data-menu-lazy]' : '[data-menu-lazy]');
}

/** @param {boolean} failed */
function showError(failed, scope = 'full') {
  const section = getSection();
  if (!section) return;
  const root = scope === 'drawer' ? section.querySelector('header-drawer') : section;
  if (!root) return;
  for (const element of root.querySelectorAll('[data-menu-lazy-loading]')) {
    if (element instanceof HTMLElement) element.hidden = failed;
  }
  for (const element of root.querySelectorAll('[data-menu-lazy-error]')) {
    if (element instanceof HTMLElement) element.hidden = !failed;
  }
}

/**
 * Tidy up after the menus land: drawer animation listeners, the focus trap
 * (it lists focusable elements when the drawer opens, so a drawer that opened
 * before the list arrived needs a fresh trap), and a heads-up for other scripts.
 * @param {Element | null} focusBefore - What had focus before the render
 */
function afterHydrate(focusBefore, scope) {
  const section = getSection();
  if (!section) return;

  for (const element of section.querySelectorAll('header-drawer .menu-drawer__animated-element')) {
    element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
  }

  const drawer = section.querySelector('header-drawer details.menu-drawer-container[open]');
  // A later full render may complete after a phone-to-desktop resize. Do not
  // focus a drawer that CSS has hidden while the shopper uses desktop menus.
  if (drawer instanceof HTMLDetailsElement && drawer.getClientRects().length && !drawer.querySelector('details.menu-open')) {
    trapFocus(drawer);
    if (focusBefore instanceof HTMLElement && focusBefore.isConnected && drawer.contains(focusBefore)) {
      focusBefore.focus({ preventScroll: true });
    } else {
      // Focus was on the retry button, which the render removed.
      const first = drawer.querySelector('.menu-drawer__navigation a[href], .menu-drawer__navigation summary');
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
    }
  }

  document.dispatchEvent(new CustomEvent('phaser:header-hydrated', { detail: { scope } }));
}

/**
 * Phaser: sectionRenderer serialises by section ID, so never start overlapping
 * drawer/full renders. A full request arriving during drawer hydration is queued.
 * @param {'drawer'|'full'} [scope]
 */
function hydrateHeader(scope = 'full') {
  if (done) return pending;
  const drawerURL = script?.getAttribute('data-phaser-drawer-url');
  // Cached pages from before the split safely retain the full-header contract.
  if (scope === 'drawer' && (!drawerURL || drawerResponseFailed)) scope = 'full';
  if (pending) {
    if (scope === 'full' && pendingScope === 'drawer') fullRequested = true;
    return pending;
  }

  const section = getSection();
  if (!section) return;
  if (!isLazy(scope)) {
    if (!isLazy()) {
      done = true;
      intentListeners.abort();
    }
    return;
  }

  const url = new URL(scope === 'drawer' ? drawerURL : window.location.href, window.location.origin);
  url.searchParams.delete('page');
  showError(false, scope);
  pendingScope = scope;
  let focusBefore = null;
  let applied = false;

  pending = sectionRenderer
    .renderSection(sectionId, {
      cache: false, url, mode: 'hydration',
      shouldRender: (html) => {
        if (scope === 'drawer' && !html?.includes('data-phaser-drawer-response')) return false;
        focusBefore = document.activeElement;
        applied = true;
        return true;
      },
    })
    .then(() => {
      // An aborted render resolves without morphing; only count a real one.
      if (!applied || isLazy(scope)) throw new Error('Header menus did not load');
      if (!isLazy()) {
        section.dataset.hydrated = 'true';
        done = true;
        intentListeners.abort();
      }
      afterHydrate(focusBefore, scope);
    })
    .catch(() => {
      if (scope === 'drawer') {
        // A missing alternate view / malformed response must not strand links.
        drawerResponseFailed = true;
        fullRequested = true;
      } else showError(true);
    })
    .finally(() => {
      pending = null;
      pendingScope = '';
      if (fullRequested) {
        fullRequested = false;
        hydrateHeader('full');
      }
    });
  return pending;
}

if (sectionId) {
  const section = getSection();

  // Delegation survives keyed hydration. Leave listeners in place after failure
  // for the next genuine menu interaction; remove them together after success.
  if (section) {
    const onIntent = (event) => {
      if (event.target instanceof Element && needsNativeMenu(event.target)) {
        hydrateHeader(event.target.closest('header-drawer') ? 'drawer' : 'full');
      }
    };
    for (const type of ['pointerover', 'focusin', 'touchstart', 'pointerdown']) {
      section.addEventListener(type, onIntent, { passive: true, signal: intentListeners.signal });
    }
    section.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-menu-lazy-retry]')) {
        hydrateHeader(event.target.closest('header-drawer') ? 'drawer' : 'full');
      }
    }, { signal: intentListeners.signal });

    // Module download can finish after the shopper has already reached the menu.
    const initialIntent = section.contains(document.activeElement) && document.activeElement?.closest(menuIntentSelector)
      ? document.activeElement
      : section.querySelector('header-menu .qd-not:hover') || section.querySelector('header-menu:hover, header-drawer:hover');
    if (initialIntent instanceof Element && needsNativeMenu(initialIntent)) {
      hydrateHeader(initialIntent.closest('header-drawer') ? 'drawer' : 'full');
    }
  }

  document.addEventListener('phaser:native-menu-needed', () => hydrateHeader('full'), { signal: intentListeners.signal });

  // Googlebot, Bingbot and the like: render the menus on idle, as every visitor used to get.
  if (/bot\b|crawl|spider|slurp|mediapartners|inspectiontool/i.test(navigator.userAgent)) {
    onDocumentReady(() => requestIdleCallback(() => hydrateHeader('full')));
  }
}
